import { CarrierVisitDirection, TruckVisitDto } from '@planning/app/api'
import _ from 'lodash'
import { computed, makeObservable } from 'mobx'
import { EntityItem } from '../base/EntityItem'
import { ICompanyItem } from '../externalDriver/CompanyItem'
import { CompanyItemStore } from '../externalDriver/CompanyItemStore'
import { IExternalDriverItem } from '../externalDriver/ExternalDriverItem'
import { ExternalDriverItemStore } from '../externalDriver/ExternalDriverItemStore'
import { GateItemStore } from '../gate/GateItemStore'
import { IOrderItem } from '../order/OrderItem'
import { ITruckItem } from '../truck/TruckItem'
import { TruckItemStore } from '../truck/TruckItemStore'
import { IEntity } from '../types'
import { TruckVisitItemStore } from './TruckVisitItemStore'

export interface ITruckVisitItem extends IEntity<TruckVisitDto> {
  orders: IOrderItem[]
  inboundOrders: IOrderItem[]
  outboundOrders: IOrderItem[]
  truck: ITruckItem | undefined
  arrival: string | null | undefined
  departure: string | null | undefined
  driver?: IExternalDriverItem
  company?: ICompanyItem
  entryGateName: string | undefined
  designatedExitGateName: string | undefined
  exitGateName: string | undefined
}
export class TruckVisitItem extends EntityItem<TruckVisitDto> implements ITruckVisitItem {
  constructor(
    id: number,
    data: TruckVisitDto,
    private itemStore: TruckVisitItemStore,
    private truckStore?: TruckItemStore,
    private externalDriverStore?: ExternalDriverItemStore,
    private companyStore?: CompanyItemStore,
    private gateStore?: GateItemStore,
  ) {
    super(id, data)
    makeObservable(this, {
      orders: computed,
      inboundOrders: computed,
      outboundOrders: computed,
      truck: computed,
      driver: computed,
      company: computed,
      arrival: computed,
      departure: computed,
      entryGateName: computed,
      designatedExitGateName: computed,
      exitGateName: computed,
    })
  }

  get orders() {
    return _.get(this.itemStore.ordersByTruckVisitId, this.id) ?? []
  }

  get inboundOrders() {
    return _.filter(this.orders, order => order.data.direction === CarrierVisitDirection.Inbound)
  }

  get outboundOrders() {
    return _.filter(this.orders, order => order.data.direction === CarrierVisitDirection.Outbound)
  }

  get truck() {
    return _.get(this.truckStore?.elements, this.data.carrierIds)
  }

  get driver() {
    if (!this.data.externalDriverId) return undefined
    return _.get(this.externalDriverStore?.elements, this.data.externalDriverId)
  }

  get company() {
    if (!this.data.truckCompanyId) return undefined
    return _.get(this.companyStore?.elements, this.data.truckCompanyId)
  }

  get entryGateName() {
    return this.getGateName(this.data.entryGateId)
  }

  get designatedExitGateName() {
    return this.getGateName(this.data.designatedExitGateId)
  }

  get exitGateName() {
    return this.getGateName(this.data.exitGateId)
  }

  get arrival() {
    return this.data.ata ?? this.data.eta
  }

  get departure() {
    return this.data.atd ?? this.data.etd
  }

  private getGateName(gateId?: string | null) {
    if (!gateId) return undefined
    return _.get(this.gateStore?.elements, gateId)?.data.name ?? undefined
  }
}
